if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.deliveryLine = this.querySelector('.js-inventory-delivery');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        this.dataset.zzDeliveryOverride === 'true'
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          inventory.zz_delivery_override === true
        );
        this.updateDelivery(inventory);
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     * ZZ: Updates the delivery time line for the selected variant
     * @param {object} inventory - variant entry from the embedded JSON
     */
    updateDelivery(inventory) {
      if (!this.deliveryLine) return;
      if (inventory && inventory.delivery) {
        this.deliveryLine.textContent = inventory.delivery;
        this.deliveryLine.hidden = false;
      } else {
        this.deliveryLine.textContent = '';
        this.deliveryLine.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {boolean} zzDeliveryOverride - ZZ: variant has its own delivery time metafields
     */
    updateInventory(count, available, inventoryPolicy, zzDeliveryOverride) {
      if (count === 0 && !available && this.hidden) {
        return;
      }

      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      // ZZ: Farb-Flag zurücksetzen — wird unten neu gesetzt, wenn der Override greift
      this.removeAttribute('data-zz-delivery-override-active');

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          // ZZ: Lager-Variante — Level-Text, Fallback auf allgemeinen ZZ-Text
          const zzVeryLowText = this.dataset.textDeliveryOverrideVeryLow || this.dataset.textDeliveryOverride;
          if (zzDeliveryOverride && zzVeryLowText) {
            this.inventoryNotice.innerText = zzVeryLowText;
            this.setAttribute('data-zz-delivery-override-active', '');
          } else {
            this.inventoryNotice.innerText = theme.strings.veryLowStock;
          }
        } else if (inventoryLevel === 'low') {
          // ZZ: Lager-Variante — Level-Text, Fallback auf allgemeinen ZZ-Text
          const zzLowText = this.dataset.textDeliveryOverrideLow || this.dataset.textDeliveryOverride;
          if (zzDeliveryOverride && zzLowText) {
            this.inventoryNotice.innerText = zzLowText;
            this.setAttribute('data-zz-delivery-override-active', '');
          } else {
            this.inventoryNotice.innerText = theme.strings.lowStock;
          }
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          // ZZ: Lager-Variante mit eigenen Lieferzeit-Metafeldern — Status-Text austauschen
          if (zzDeliveryOverride && this.dataset.textDeliveryOverride) {
            this.inventoryNotice.innerText = this.dataset.textDeliveryOverride;
            this.setAttribute('data-zz-delivery-override-active', '');
          } else {
            this.inventoryNotice.innerText = theme.strings.inStock;
          }
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
